"use client";

import React, {
  ChangeEvent,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
} from "react";

import { useIsAuthenticated } from "@azure/msal-react";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import { clsx } from "clsx";
import { isEmpty, omit } from "lodash";
import dynamic from "next/dynamic";
import { useParams, usePathname, useSearchParams, useRouter } from "next/navigation";

import { GlobalPathParams } from "~/app/[locale]/types";
import { ColorPaletteProps } from "~/bff/components/ColorPalette";
import { SearchResultsPageProps } from "~/bff/components/SearchResultsPage";
import { Docs } from "~/bff/types/Docs";
import { FacetCountsFacetFieldsCategoryFacet } from "~/bff/types/FacetCountsFacetFieldsCategoryFacet";
import { ShoppingTypeFilter } from "~/bff/types/ShoppingTypeFilter";
import { SortOptions } from "~/bff/types/SortOptions";
import { WidgetFacetRange } from "~/bff/types/WidgetFacetRange";
import { BreadcrumbsPanel } from "~/components/breadcrumbs-panel/component";
import {
  getSortingParams,
  getSortOrderForGA,
  updateUrlHash,
} from "~/components/plp-page/helpers";
import { AppliedFilters } from "~/components/products-layout/components/products-filters/types";
import {
  DEFAULT_PAGE_SIZE as DEFAULT_PRODUCTS_PAGE_SIZE,
  FILTER_DIVIDER,
} from "~/components/products-layout/constants";
import {
  BloomreachCatalogsAzureKeys,
  BloomreachPageType,
} from "~/constants/bloomreach";
import { getCurrencyCodeByLocale } from "~/constants/i18n";
import { Params, RouteBase, Variables } from "~/constants/request";
import { AuthContext } from "~/context/auth/context";
import { DictionaryLabelContext } from "~/context/dictionary-label/context";
import { DictionaryKeys } from "~/context/dictionary-label/types";
import { RouterHistoryContext } from "~/context/router-history/context";
import { waitForRouterHistory } from "~/context/router-history/helper";
import { useVariable } from "~/context/variables/hooks/use-variable";
import { GET_BLOOMREACH_SEARCH_BY_KEYWORD_QUERY } from "~/graphql/queries/getBloomreachSearchByKeyword";
import { applyFiltersHash } from "~/helpers/apply-filters-hash";
import { checkAndSetStoreByLocation } from "~/helpers/check-and-set-store-by-location";
import { importComponentByName } from "~/helpers/import-component-by-name";
import { getSelectedOrPreferredStoreId } from "~/helpers/selected-store/get-selected-or-preferred-store-id";
import { getTestAutomationProps } from "~/helpers/test-automation-props";
import { removePageFromUrl } from "~/helpers/use-remove-page-from-url/helper";
import { useGetAnchor } from "~/hooks/anchoring/hook";
import { Anchoring } from "~/hooks/anchoring/types";
import { useInitialScrollTop } from "~/hooks/anchoring/use-initial-scroll-top";
import { useGetPageNumberRef } from "~/hooks/use-get-page-number-ref/component";
import { useAzureConfigurator } from "~/services/azure-configurator/use-azure-configurator";
import { Nullable } from "~/types/general.types";
import {
  IHashFilterParams,
  IUseRequestHandlerActionsKind,
  ProductRequestType,
} from "~/types/products-request-handler";
import { LoadingDirection } from "~/types/requestHandlers";
import { getBrUid2Client } from "~/utils/data/bloomreach/get-br-uid2-client";
import { getRefUrlClient } from "~/utils/data/bloomreach/get-ref-url-client";
import { getJsonLdForSrp } from "~/utils/jsonLd/product-list";

import { PageType } from "__generated__/globalTypes";

import { ProductsLayout } from "../products-layout/component";
import { mappedFacetsRangesForFilter } from "../rbp-page/helpers";
import { SeoMetaTags } from "../seo-meta-tags/component";

import type { NoResultsProps } from "./components/no-results-page/component";
import {
  DEFAULT_TEST_AUTOMATION_ID,
  SEARCH_RESULTS_PAGE_CLASSES,
} from "./constants";
import { useBloomreachPixelEvent } from "./hooks/use-bloomreach-pixel-event";
import { useDataForGoogleAnalytics } from "./hooks/use-data-for-google-analytics";
import { useSrpProductsRequestHandler } from "./hooks/use-srp-products-request-handler";
import { IUseRequestHandler } from "./hooks/use-srp-products-request-handler/types";
import { SrpSkeleton } from "./skeleton";
import { SearchResultsPageContainer } from "./styled";
import { SearchTabs } from "./types";

const NoResults = dynamic<NoResultsProps>(async () =>
  importComponentByName(
    import("./components/no-results-page/component"),
    "NoResults",
  ),
);

export const SearchResultsPage: React.FC<SearchResultsPageProps> = ({
  breadcrumbs,
  productsCarousel,
  productsData,
  translations,
  testAutomationId,
  shoppingTypeFilter,
  sortOptions,
  recentlyViewedProducts,
  seo,
}) => {
  const { locale } = useParams<GlobalPathParams>();
  const router = useRouter();
  const pageLocale = locale;
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const urlPrefix = useVariable<string>(Variables.URL_PREFIX);
  const colorPalette = useVariable<ColorPaletteProps>(Variables.COLOR_PALETTE);
  const dictionary = useContext(DictionaryLabelContext);
  const account = useContext(AuthContext)?.account;
  const previousPath = useContext(RouterHistoryContext)?.previousPath;
  const config = useAzureConfigurator(locale);
  const isClickAndCollectEnabled = useMemo(
    () => config?.featureFlags?.clickAndCollect?.enabled,
    [config],
  );

  const currencyCode = useMemo(() => getCurrencyCodeByLocale(locale), [locale]);

  // page response
  const {
    pageTitle: PAGE_TITLE,
    productFilterPanelText: PRODUCT_FILTER_PANEL_TEXT,
    productSortSelectorText: PRODUCT_SORT_SELECTOR_TEXT,
    searchResultsFound: SEARCH_RESULTS_FOUND,
    noSearchResultsFound,
    noProductsSearchResultsFound,
    noSearchResultsSubtitle: NO_SEARCH_RESULTS_SUBTITLE,
    searchSuggestion: SEARCH_SUGGESTION,
    productFilterClearAllButtonText: PRODUCT_FILTER_CLEAR_ALL_BUTTON_TEXT,
    productCounterText: PRODUCT_COUNTER_TEXT,
  } = translations ?? {};

  // no results page response
  const { noSearchResultsTitle: NO_SEARCH_RESULTS_FOUND } =
    noSearchResultsFound ?? {};
  const { noSearchResultsTitle: NO_PRODUCTS_SEARCH_RESULTS_FOUND } =
    noProductsSearchResultsFound ?? {};

  // products data response
  const {
    response: productsDataResponse,
    facet_counts,
    did_you_mean: productsSuggestions = [],
  } = productsData ?? {};

  const { docs: productsDocs = [], numFound: TOTAL_PRODUCTS_COUNT = 0 } =
    productsDataResponse ?? {};

  const {
    facet_fields: productsFacetFields = [],
    facet_ranges: productsFacetRanges = [],
  } = facet_counts ?? {};

  // url query params
  const queryParam = useMemo(
    () => (searchParams.get(Params.QUERY) as string) ?? "",
    [searchParams],
  );

  const oldQueryParamRef = useRef(searchParams.get(Params.QUERY) ?? "");

  const currentTab = useMemo(
    () => (searchParams.get(Params.TAB) as SearchTabs) ?? SearchTabs.PRODUCTS,
    [searchParams],
  );

  const productsTab: Anchoring["page"] = "SRP_PRODUCTS";
  const [productsMarkerId, setProductsMarkerId] = useGetAnchor(productsTab);

  //Refs
  const pageNumber = useGetPageNumberRef();
  const scrollRestorationRef =
    useRef<Nullable<HTMLSpanElement & HTMLAnchorElement>>(null);

  const brUid2 = getBrUid2Client(locale, PageType.SearchResults);
  const brRefUrl = getRefUrlClient(locale, PageType.SearchResults);

  const productsPageNumber = useRef<number>(pageNumber.current || 1);
  const direction = useRef<LoadingDirection>("");
  const isBrowserNavigating = useRef(false);

  useInitialScrollTop([RouteBase.PDP, RouteBase.ARTICLE]);

  // Products
  const initialProductsMap = useMemo(
    () =>
      new Map<string, number>(
        (productsDocs ?? [])
          .filter((p) => p?.pid !== null && p?.pid !== undefined)
          .map((p) => [p?.pid as string, productsPageNumber.current]),
      ),
    [productsDocs],
  );

  const productsRequestInitialState: IUseRequestHandler = useMemo(
    () => ({
      filters: {
        ...mappedFacetsRangesForFilter(
          productsFacetFields as FacetCountsFacetFieldsCategoryFacet[],
        ),
        ...mappedFacetsRangesForFilter(productsFacetRanges as WidgetFacetRange[]),
      },
      items: productsDocs ?? [],
      numFound: TOTAL_PRODUCTS_COUNT,
      handlerParams: {
        isShopFilter: null,
        currentStore: null,
        direction: direction.current,
        currentPageNumber: productsPageNumber,
        activeSortingParam: getSortingParams(sortOptions?.props as SortOptions)[0]
          .value,
        appliedFilters: null,
        itemsMap: initialProductsMap,
        pageSize: DEFAULT_PRODUCTS_PAGE_SIZE,
        productsMarkerId,
        setProductsMarkerId,
        isUiLoading: !productsData,
      },
    }),
    [
      TOTAL_PRODUCTS_COUNT,
      initialProductsMap,
      productsData,
      productsDocs,
      productsFacetFields,
      productsFacetRanges,
      productsMarkerId,
      setProductsMarkerId,
      sortOptions?.props,
    ],
  );
  const {
    efq,
    isLoading: areProductsLoading,
    getMoreItems: getMoreProducts,
    isShopFilter,
    currentStore,
    data: {
      filters,
      items: products,
      numFound: totalProductsCount,
      handlerParams: { activeSortingParam, appliedFilters, itemsMap: productsMap },
    },
    setShopFilter,
    setCurrentStore,
    handleUpdateStore,
    handleShopFilterClick,
    dispatchRequest,
    handleLoadMore: handleProductsLoadMore,
    handleLoadPrevious: handleProductsLoadPrevious,
    onActiveSortingParamSet,
    shopFilterSortingParam,
    setShopFilterSortingParam,
    handleShopFilterChipClick,
    onShopFilterDropdown,
    isUiLoading,
  } = useSrpProductsRequestHandler(
    GET_BLOOMREACH_SEARCH_BY_KEYWORD_QUERY,
    productsRequestInitialState,
    {
      q: queryParam,
      locale: pageLocale,
      brUid2,
      brRefUrl,
    },
  );

  const getParams: () => IHashFilterParams = useCallback(
    () => ({
      isShopFilter,
      appliedFilters,
      activeSortingParam,
      productType: shopFilterSortingParam,
      selectedStoreId: currentStore?.id,
      config: {
        pageNumber: productsPageNumber.current,
        getMoreItems: getMoreProducts,
        variables: {
          locale: pageLocale,
          q: queryParam,
          efq,
          brUid2,
          brRefUrl,
        },
      },
    }),
    [
      isShopFilter,
      appliedFilters,
      activeSortingParam,
      shopFilterSortingParam,
      currentStore?.id,
      getMoreProducts,
      pageLocale,
      queryParam,
      efq,
      brUid2,
      brRefUrl,
    ],
  );

  const productsCounterCaption = useMemo(
    () => `${totalProductsCount} ${PRODUCT_COUNTER_TEXT}`,
    [totalProductsCount, PRODUCT_COUNTER_TEXT],
  );

  const showProductsPreviousButton = useMemo(
    () => Array.from(productsMap.values())?.[0] > 1,
    [productsMap],
  );

  useEffect(() => {
    const checkAndSetStore = async () => {
      const storeId = getSelectedOrPreferredStoreId(account);
      const params = {
        storeId,
        locale: pageLocale,
        currentStore,
        searchParams,
        pathname,
      };
      checkAndSetStoreByLocation(params, {
        setStore: setCurrentStore,
        setShopFilter,
        setShopFilterSortingParam,
      });
    };
    checkAndSetStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (productsData) {
      return;
    }

    const params = getParams();
    queueMicrotask(() => {
      dispatchRequest({
        type: IUseRequestHandlerActionsKind.FULL_OVERRIDE,
        payload: {
          props: {
            appliedFilters: null,
            activeSortingParam,
            isShopFilter: null,
            isUiLoading: true,
          },
          updateFunc: updateUrlHash,
          params: {
            ...params,
            config: {
              ...params.config,
              variables: {
                ...params.config.variables,
                shouldConcat: true,
                start:
                  ((params.config.pageNumber ?? productsPageNumber.current ?? 1) -
                    1) *
                  DEFAULT_PRODUCTS_PAGE_SIZE,
                brRefUrl: getRefUrlClient(pageLocale, PageType.SearchResults),
              },
            },
          },
        },
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    // Handle navigation by history back or forvard to reaply filters from hash
    if (isBrowserNavigating.current) {
      isBrowserNavigating.current = false;
      readFromHashAndApply();
      return;
    }
    if (oldQueryParamRef.current === queryParam) {
      return;
    }
    oldQueryParamRef.current = queryParam;
    productsPageNumber.current = 1;
    readFromHashAndApply();
    removePageFromUrl(searchParams, pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryParam]);

  const handleFiltersClick = useCallback(
    (event: ChangeEvent<HTMLInputElement>, filter?: string) => {
      if (productsMarkerId) {
        setProductsMarkerId(null);
      }
      const params = getParams();
      productsPageNumber.current = 1;
      const filterName = filter && filter?.length > 1 ? filter : event.target.name;
      const filterValue = Number(event.target.value || event.currentTarget.value);
      const isSwatch = filterName.split(FILTER_DIVIDER)[3] === "swatch";
      const appliedFilter: [string, number | boolean] = appliedFilters?.[filterName]
        ? [filterName, false]
        : [filterName, filterValue];

      let appliedFiltersPayload: AppliedFilters = {
        ...appliedFilters,
        [appliedFilter[0]]: appliedFilter[1],
      };

      if (isSwatch && !isEmpty(appliedFilters)) {
        const filterCategoryName = filterName.split(FILTER_DIVIDER)[0];
        Object.keys(appliedFilters).forEach((key) => {
          if (key.split(FILTER_DIVIDER)[0] === filterCategoryName) {
            appliedFiltersPayload = {
              ...appliedFiltersPayload,
              ...omit({ ...appliedFilters }, [key]),
            };
          }
        });
      }

      dispatchRequest({
        type: IUseRequestHandlerActionsKind.UPDATE_APPLIED_FILTER_ON_CLICK,
        payload: {
          appliedFilter,
          updateFunc: updateUrlHash,
          params: {
            ...params,
            appliedFilters: appliedFiltersPayload,
          },
          GAdata: {
            breadcrumbs,
          },
        },
      });
      removePageFromUrl(searchParams, pathname);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      searchParams,
      pathname,
      productsMarkerId,
      getParams,
      appliedFilters,
      dispatchRequest,
      setProductsMarkerId,
    ],
  );

  const handleAllFiltersClear = useCallback(() => {
    if (productsMarkerId) {
      setProductsMarkerId(null);
    }
    productsPageNumber.current = 1;
    const params = getParams();
    updateUrlHash({
      ...params,
      isShopFilter: false,
      appliedFilters: null,
      config: {
        ...params.config,
        variables: {
          ...params.config.variables,
          efq: isClickAndCollectEnabled ? null : efq,
        },
      },
    });
    setShopFilter(false);
    dispatchRequest({
      type: IUseRequestHandlerActionsKind.CLEAR_APPLIED_FILTERS,
      payload: null,
    });
    removePageFromUrl(searchParams, pathname);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    searchParams,
    pathname,
    dispatchRequest,
    getParams,
    productsMarkerId,
    setProductsMarkerId,
    setShopFilter,
  ]);

  const handleChipClick = useCallback(
    (filterName: string) => {
      if (productsMarkerId) {
        setProductsMarkerId(null);
      }
      productsPageNumber.current = 1;
      const params = getParams();
      dispatchRequest({
        type: IUseRequestHandlerActionsKind.REMOVE_APPLIED_FILTER_ON_CLICK,
        payload: {
          appliedFilter: [filterName, false],
          updateFunc: updateUrlHash,
          params: {
            ...params,
            appliedFilters: omit({ ...appliedFilters }, [filterName]),
          },
        },
      });
      removePageFromUrl(searchParams, pathname);
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      searchParams,
      pathname,
      productsMarkerId,
      getParams,
      dispatchRequest,
      appliedFilters,
      setProductsMarkerId,
    ],
  );

  const readFromHashAndApply = useCallback(() => {
    const getFiltersFromHash = (
      activeSortingParam: string,
      appliedFilters: AppliedFilters,
      isShop: boolean | null,
      productType: ProductRequestType,
    ) => {
      const params = {
        activeSortingParam,
        productType,
        appliedFilters,
        isShopFilter: isShop,
        selectedStoreId: currentStore?.id,
        config: {
          pageNumber: productsPageNumber.current,
          getMoreItems: getMoreProducts,
          variables: {
            locale: pageLocale,
            q: queryParam,
            efq,
            brUid2,
          },
        },
      };
      setShopFilter(isShop);
      setCurrentStore(currentStore);
      if (isShop) {
        setShopFilterSortingParam(productType || ProductRequestType.AllProducts);
      }
      waitForRouterHistory(() => {
        dispatchRequest({
          type: IUseRequestHandlerActionsKind.FULL_OVERRIDE,
          payload: {
            props: {
              appliedFilters,
              activeSortingParam,
              isShopFilter: isShop,
              isUiLoading: true,
            },
            updateFunc: updateUrlHash,
            params: {
              ...params,
              config: {
                ...params.config,
                variables: {
                  ...params.config.variables,
                  shouldConcat: true,
                  start: (params.config.pageNumber - 1) * DEFAULT_PRODUCTS_PAGE_SIZE,
                  brRefUrl: getRefUrlClient(pageLocale, PageType.SearchResults),
                },
              },
            },
          },
        });
      });
    };
    applyFiltersHash(location.hash, {
      applyFunc: getFiltersFromHash,
    });
  }, [
    currentStore,
    dispatchRequest,
    setShopFilterSortingParam,
    getMoreProducts,
    pageLocale,
    queryParam,
    setCurrentStore,
    setShopFilter,
    efq,
    brUid2,
  ]);

  useEffect(() => {
    productsPageNumber.current = Number(searchParams.get(Params.PAGE) || 1);
  }, [searchParams]);

  useEffect(() => {
    if (!location.hash) {
      return;
    }
    readFromHashAndApply();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStore?.id]);

  useEffect(() => {
    function popstateHandler(event: PopStateEvent) {
      const as = event.state?.as ?? pathname;
      if (as !== pathname) {
        return;
      }

      const page = Number(searchParams.get(Params.PAGE)) || 1;
      scrollRestorationRef.current = null;
      if (as.includes(`tab=${SearchTabs.PRODUCTS}`)) {
        isBrowserNavigating.current = true;
        productsPageNumber.current = page;
        dispatchRequest({
          type: IUseRequestHandlerActionsKind.CLEAR_MAP,
          payload: [],
        });
        readFromHashAndApply();
      }
    }

    // eslint-disable-next-line no-restricted-globals
    window.addEventListener("popstate", popstateHandler);
    return () => {
      // eslint-disable-next-line no-restricted-globals
      window.removeEventListener("popstate", popstateHandler);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    dispatchRequest,
    pageLocale,
    queryParam,
    readFromHashAndApply,
    router,
    brUid2,
    brRefUrl,
    pathname,
  ]);

  const scrollHandler = useCallback(() => {
    if (["next", "previous"].includes(direction.current) || productsMarkerId) {
      return;
    }
    scrollTo({ top: 0 });
  }, [productsMarkerId]);

  useEffect(() => {
    if (productsMap.size === 0 || productsMap.size === DEFAULT_PRODUCTS_PAGE_SIZE) {
      scrollHandler();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [productsMap.size, currentTab]);

  const productsDocsFiltered = productsDocs
    ? productsDocs.filter(
        (item): item is Docs => item !== null && item !== undefined,
      )
    : [];

  const jsonLDConfig = getJsonLdForSrp(
    productsDocsFiltered,
    currencyCode,
    productsMap,
    urlPrefix,
    dictionary?.[DictionaryKeys.BrandName] ?? "",
  );

  const isUserAuthenticated = useIsAuthenticated();
  const sortOrderForGA = getSortOrderForGA(activeSortingParam);

  useDataForGoogleAnalytics({
    locale: pageLocale,
    queryParam: String(queryParam),
    totalProductsCount,
    products,
    isUserAuthenticated,
    sortOrderForGA,
    pageSize: DEFAULT_PRODUCTS_PAGE_SIZE,
  });

  useBloomreachPixelEvent({
    pageType: BloomreachPageType.SearchResultsPage,
    catalogsAzureKeys: BloomreachCatalogsAzureKeys[currentTab],
    queryParam,
    title: `${queryParam} | ${dictionary?.[DictionaryKeys.BrandName]}`,
    pageLocale,
    currentTab,
    account,
    previousPath,
  });

  if (isUiLoading) {
    return <SrpSkeleton />;
  }

  if (!totalProductsCount) {
    return (
      <NoResults
        title={NO_SEARCH_RESULTS_FOUND}
        subTitle={NO_SEARCH_RESULTS_SUBTITLE}
        suggestionLabel={SEARCH_SUGGESTION}
        suggestions={productsSuggestions}
      >
        {productsCarousel as React.ReactElement}
      </NoResults>
    );
  }

  return (
    <>
      {seo?.jsonLd && (
        <SeoMetaTags
          jsonLd={seo.jsonLd}
          extraProps={{ jsonLDConfig: jsonLDConfig }}
        />
      )}
      <SearchResultsPageContainer
        id={testAutomationId ?? DEFAULT_TEST_AUTOMATION_ID}
        {...getTestAutomationProps(testAutomationId ?? DEFAULT_TEST_AUTOMATION_ID)}
      >
        {breadcrumbs && <BreadcrumbsPanel breadcrumbs={breadcrumbs} />}
        <Typography
          id="search-results-header"
          className={SEARCH_RESULTS_PAGE_CLASSES.header}
          variant="h1Medium"
        >
          {PAGE_TITLE} “{queryParam}”
          <Box className={SEARCH_RESULTS_PAGE_CLASSES.counter}>
            {totalProductsCount}
          </Box>
        </Typography>
        <Box
          id="search-results-products-tab"
          className={clsx(SEARCH_RESULTS_PAGE_CLASSES.content, {
            [SEARCH_RESULTS_PAGE_CLASSES.active]: currentTab === SearchTabs.PRODUCTS,
          })}
        >
          {!totalProductsCount ? (
            <NoResults
              title={NO_PRODUCTS_SEARCH_RESULTS_FOUND}
              subTitle={NO_SEARCH_RESULTS_SUBTITLE}
              suggestionLabel={SEARCH_SUGGESTION}
              suggestions={productsSuggestions}
            >
              {productsCarousel as React.ReactElement}
            </NoResults>
          ) : (
            <ProductsLayout
              isLoading={areProductsLoading}
              onLoadMore={handleProductsLoadMore}
              setActiveSortingParam={onActiveSortingParamSet}
              activeSortingParam={activeSortingParam}
              sortingParams={getSortingParams(sortOptions?.props as SortOptions)}
              products={products}
              totalCount={totalProductsCount}
              productsCaption={productsCounterCaption}
              productFilterPanelText={PRODUCT_FILTER_PANEL_TEXT}
              productSortSelectorText={PRODUCT_SORT_SELECTOR_TEXT}
              searchResultsFound={SEARCH_RESULTS_FOUND}
              loadPreviousButtonText={SEARCH_RESULTS_FOUND?.loadPreviousButtonText}
              loadMoreButtonText={SEARCH_RESULTS_FOUND?.loadMoreButtonText}
              progressBarText={SEARCH_RESULTS_FOUND?.progressBarText}
              filtersData={filters}
              appliedFilters={appliedFilters}
              onFiltersClick={handleFiltersClick}
              onAllFiltersClear={handleAllFiltersClear}
              onChipClick={handleChipClick}
              colorPalette={colorPalette}
              onShopFilterClick={handleShopFilterClick}
              isShopFilter={isShopFilter}
              currentStore={currentStore}
              onUpdateStore={handleUpdateStore}
              breadcrumbs={breadcrumbs}
              pageNumber={productsPageNumber}
              productToPageMap={productsMap}
              showPreviousButton={showProductsPreviousButton}
              onLoadPrevious={handleProductsLoadPrevious}
              restorationRef={{ markerId: productsMarkerId, scrollRestorationRef }}
              productFilterClearAllButtonText={PRODUCT_FILTER_CLEAR_ALL_BUTTON_TEXT}
              onShopFilterDropdown={onShopFilterDropdown}
              shopFilterSortingParam={shopFilterSortingParam}
              setShopFilterSortingParam={setShopFilterSortingParam}
              onShopFilterChipClick={handleShopFilterChipClick}
              shopFilter={shoppingTypeFilter?.props as ShoppingTypeFilter} //TODO: tech debt - specified prop type manually after Universal component rendering
            />
          )}
          {recentlyViewedProducts as React.ReactElement}
        </Box>
      </SearchResultsPageContainer>
    </>
  );
};
